import { useEffect } from "react";
import { Link } from "react-router-dom";
import Logo from "@/components/Logo";
import SiteFooter from "@/components/SiteFooter";
import {
  Sparkles,
  Search,
  Bot,
  Compass,
  TrendingUp,
  Target,
  CheckCircle2,
  ArrowRight,
  HelpCircle,
  MapPin,
  Rocket,
} from "lucide-react";

const PAGE_TITLE =
  "GEO Nedir? Generative Engine Optimization Ne Anlama Gelir? | uSeen";
const PAGE_DESC =
  "GEO, markaların ChatGPT, Gemini, Google AI ve diğer yapay zekâ destekli cevap motorlarında görünür olmasını hedefleyen yeni optimizasyon yaklaşımıdır.";

const FAQ_ITEMS = [
  {
    q: "GEO nedir?",
    a: "GEO, Generative Engine Optimization ifadesinin kısaltmasıdır. Yapay zekâ destekli cevap motorlarında daha görünür ve önerilebilir olmak için yapılan optimizasyon çalışmalarını ifade eder.",
  },
  {
    q: "GEO ile SEO aynı şey mi?",
    a: "Hayır. SEO, Google gibi arama motorlarında üst sıralarda görünmeye odaklanır. GEO ise ChatGPT, Gemini, Google AI ve benzeri yapay zekâ sistemlerinin cevaplarında yer almayı hedefler.",
  },
  {
    q: "GEO kimler için önemlidir?",
    a: "Yerel işletmeler, hizmet firmaları, restoranlar, klinikler, oteller, güzellik merkezleri, e-ticaret markaları ve dijitalde görünür olmak isteyen tüm işletmeler için önemlidir.",
  },
  {
    q: "GEO çalışması nasıl yapılır?",
    a: "Web sitesi, işletme bilgileri, Google Business Profile, müşteri yorumları, içerikler, sosyal medya varlığı ve üçüncü parti kaynaklardaki marka görünürlüğü birlikte analiz edilir ve iyileştirilir.",
  },
  {
    q: "uSeen GEO için ne sağlar?",
    a: "uSeen, işletmenizin yapay zekâlarda ne kadar görünür olduğunu analiz eder, eksikleri belirler, rakiplerle kıyaslar ve görünürlüğü artırmak için uygulanabilir görevler oluşturur.",
  },
  {
    q: "GEO sadece büyük markalar için mi gereklidir?",
    a: "Hayır. Özellikle yerel işletmeler için GEO giderek daha önemli hale geliyor. Çünkü kullanıcılar yapay zekâlara doğrudan 'yakınımdaki en iyi işletme' veya 'bu bölgede güvenilir hizmet' gibi sorular soruyor.",
  },
];

/**
 * Public SEO landing page — /geo-nedir
 * Mirrors the structure of /ai-visibility-nedir: head meta override + FAQPage JSON-LD on mount,
 * restored on unmount. No auth, no backend calls.
 */
export default function GeoNedir() {
  useEffect(() => {
    const prevTitle = document.title;
    const metaDesc = document.querySelector('meta[name="description"]');
    const prevDesc = metaDesc?.getAttribute("content");
    document.title = PAGE_TITLE;
    if (metaDesc) metaDesc.setAttribute("content", PAGE_DESC);

    const tags = [
      ["property", "og:title", PAGE_TITLE],
      ["property", "og:description", PAGE_DESC],
      ["property", "og:type", "article"],
      ["name", "twitter:title", PAGE_TITLE],
      ["name", "twitter:description", PAGE_DESC],
    ];
    const added = [];
    for (const [attr, key, val] of tags) {
      let tag = document.querySelector(`meta[${attr}="${key}"]`);
      if (!tag) {
        tag = document.createElement("meta");
        tag.setAttribute(attr, key);
        document.head.appendChild(tag);
        added.push(tag);
      }
      tag.setAttribute("content", val);
    }

    const ld = document.createElement("script");
    ld.type = "application/ld+json";
    ld.setAttribute("data-useen-faq", "geo");
    ld.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: FAQ_ITEMS.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    });
    document.head.appendChild(ld);

    return () => {
      document.title = prevTitle;
      if (metaDesc && prevDesc !== undefined && prevDesc !== null) {
        metaDesc.setAttribute("content", prevDesc);
      }
      added.forEach((t) => t.parentNode?.removeChild(t));
      ld.parentNode?.removeChild(ld);
    };
  }, []);

  return (
    <div className="min-h-screen bg-[#0a0a14] text-slate-200 flex flex-col" data-testid="geo-nedir-page">
      <div className="absolute inset-0 bg-grid opacity-[0.08] pointer-events-none" />

      <header className="relative z-10 px-6 lg:px-12 py-6 flex items-center justify-between">
        <Link to="/" className="inline-flex items-center"><Logo /></Link>
        <nav className="flex items-center gap-5 text-sm">
          <Link to="/fiyatlandirma" className="text-slate-300 hover:text-cyan-300">Fiyatlandırma</Link>
          <Link to="/giris" className="text-slate-300 hover:text-cyan-300">Giriş Yap</Link>
          <Link to="/kayit" className="neon-btn rounded-lg px-4 py-2 text-white text-sm font-medium" data-testid="header-cta-register">
            Ücretsiz Başla
          </Link>
        </nav>
      </header>

      <main className="relative z-10 flex-1 px-6 lg:px-12 py-12">
        <div className="max-w-3xl mx-auto">
          {/* Hero */}
          <div className="text-[11px] uppercase tracking-[0.25em] text-cyan-300 mb-3 inline-flex items-center gap-1.5">
            <Sparkles size={11} /> GEO · Rehber
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-[1.05] mb-6">
            GEO Nedir?
          </h1>
          <p className="text-slate-300 text-lg leading-relaxed mb-3">
            GEO, <strong className="text-white">"Generative Engine Optimization"</strong> ifadesinin
            kısaltmasıdır. Türkçeye <em>"Üretken Yapay Zekâ Motoru Optimizasyonu"</em> veya daha anlaşılır
            şekilde <em>"yapay zekâ cevap motorları için görünürlük optimizasyonu"</em> olarak çevrilebilir.
          </p>
          <p className="text-slate-400 leading-relaxed mb-6">
            Klasik SEO, web sitelerinin Google gibi arama motorlarında daha üst sıralarda görünmesini
            hedefler. GEO ise markaların ChatGPT, Gemini, Google AI, Perplexity ve benzeri yapay zekâ
            sistemlerinin verdiği cevaplarda daha görünür ve önerilebilir olmasını hedefler.
          </p>

          <div className="not-prose grid sm:grid-cols-2 gap-3 my-6">
            <ComparisonCard
              badge="SEO"
              badgeColor="text-slate-300 border-slate-500/30 bg-slate-500/10"
              title="Arama sonuçlarında görünmek"
              desc="Google'da klasik 10 mavi linkin içinde yer almak."
            />
            <ComparisonCard
              badge="GEO"
              badgeColor="text-cyan-300 border-cyan-500/30 bg-cyan-500/10"
              title="Yapay zekâ cevaplarında yer almak"
              desc="ChatGPT / Gemini cevaplarında önerilebilir kaynak olmak."
            />
          </div>

          <Article>
            <Section icon={<TrendingUp size={18} />} title="GEO Neden Ortaya Çıktı?">
              <p>
                GEO, kullanıcı davranışları değiştiği için ortaya çıktı.
              </p>
              <p>
                Eskiden kullanıcılar Google'a bir şey yazar, çıkan sonuçları tek tek incelerdi.
                Şimdi ise birçok kullanıcı doğrudan yapay zekâya soru soruyor.
              </p>
              <p>Örneğin:</p>
              <BulletList items={[
                "Bana İstanbul'da iyi bir diş kliniği öner.",
                "Tuzla'da makarna yiyebileceğim güzel bir yer var mı?",
                "En güvenilir kamp alanları hangileri?",
                "Bu konuda hizmet veren iyi firmalar kimler?",
              ]} />
              <p>
                Bu soruların cevabında yer almak, yeni dönemin dijital rekabet avantajıdır.
                İşte <strong className="text-white">GEO, bu cevaplarda görünür olabilmek için yapılan
                çalışmaların bütünüdür.</strong>
              </p>
            </Section>

            <Section icon={<Search size={18} />} title="GEO ile SEO Arasındaki Fark Nedir?">
              <p>SEO ve GEO birbirine bağlıdır ama aynı şey değildir.</p>
              <p>
                <strong className="text-white">SEO'da amaç</strong>, belirli anahtar kelimelerde
                Google'da üst sıralarda görünmektir.
              </p>
              <p>
                <strong className="text-white">GEO'da amaç</strong>, yapay zekâların verdiği cevaplarda
                markanızın, işletmenizin veya içeriğinizin güvenilir bir kaynak ya da öneri olarak
                yer almasıdır.
              </p>
              <p>Basit fark şudur:</p>
              <div className="not-prose my-5 space-y-2">
                <div className="rounded-xl border border-slate-500/20 bg-slate-500/5 px-4 py-3 text-slate-300">
                  <span className="text-[11px] uppercase tracking-wider text-slate-400 mr-2">SEO</span>
                  Kullanıcı arama sonucuna tıklar.
                </div>
                <div className="rounded-xl border border-cyan-500/30 bg-cyan-500/10 px-4 py-3 text-white">
                  <span className="text-[11px] uppercase tracking-wider text-cyan-300 mr-2">GEO</span>
                  Kullanıcı cevabı doğrudan yapay zekâdan alır.
                </div>
              </div>
              <p>
                Bu yüzden GEO'da sadece sıralama değil; markanın nasıl anlaşıldığı, hangi kaynaklarda
                geçtiği, bilgilerinin tutarlı olup olmadığı ve güvenilirliği daha fazla önem kazanır.
              </p>
            </Section>

            <Section icon={<Compass size={18} />} title="GEO Hangi Alanlara Bakar?">
              <p>
                GEO çalışmaları birçok farklı dijital sinyali birlikte değerlendirir.
                Başlıca alanlar şunlardır:
              </p>
              <BulletList items={[
                "Web sitesindeki açıklayıcı içerikler",
                "Soru-cevap formatındaki bilgi sayfaları",
                "Google Business Profile bilgileri",
                "Müşteri yorumları",
                "Markanın farklı platformlardaki görünürlüğü",
                "Üçüncü parti kaynaklardaki marka bahsi",
                "Sosyal medya profilleri",
                "İçeriklerin net, anlaşılır ve güvenilir olması",
                "Hizmetlerin açık şekilde tanımlanması",
                "Yapılandırılmış veri kullanımı",
                "Rakiplerle karşılaştırmalı dijital görünürlük",
              ]} />
              <p>
                GEO yalnızca teknik bir çalışma değildir. Aynı zamanda
                <strong className="text-white"> marka anlatımı, içerik stratejisi ve dijital güven inşasıdır.</strong>
              </p>
            </Section>

            <Section icon={<MapPin size={18} />} title="GEO Yerel İşletmeler İçin Neden Önemli?">
              <p>
                Yerel işletmeler için GEO çok büyük bir fırsattır. Çünkü kullanıcılar yapay zekâlara
                giderek daha fazla yerel öneri soruyor. Örneğin:
              </p>
              <BulletList items={[
                "Yakınımdaki en iyi kahvaltı mekanı neresi?",
                "Anadolu Yakası'nda iyi bir güzellik merkezi öner.",
                "İstanbul'da çocuk dostu otel önerir misin?",
                "Tuzla'da güvenilir oto servis hangisi?",
                "Şile'de denize yakın kamp alanı var mı?",
              ]} />
              <p>
                Bu sorulara verilen cevaplarda yer almak,
                <strong className="text-white"> doğrudan müşteri kazanımı anlamına gelebilir.</strong>
              </p>
              <p>
                Bu nedenle yerel işletmelerin sadece Google Haritalar'da değil, yapay zekâ destekli
                öneri sistemlerinde de görünür olması gerekir.
              </p>
            </Section>

            <Section icon={<Target size={18} />} title="GEO İçin Ne Yapmak Gerekir?">
              <p>GEO için yapılması gereken temel çalışmalar şunlardır:</p>
              <Step n={1} title="İşletme Bilgilerini Netleştirmek">
                İşletmenin ne yaptığı, nerede hizmet verdiği, hangi konuda iyi olduğu açıkça
                anlatılmalıdır.
              </Step>
              <Step n={2} title="Web Sitesini Anlaşılır Hale Getirmek">
                Web sitesinde hizmetler, konum, iletişim, fiyat yaklaşımı, uzmanlık alanları ve
                sık sorulan sorular net şekilde bulunmalıdır.
              </Step>
              <Step n={3} title="Soru-Cevap İçerikleri Üretmek">
                Yapay zekâlar soru formatındaki içerikleri daha kolay anlayabilir. Bu yüzden
                kullanıcıların sorduğu gerçek sorulara cevap veren sayfalar hazırlanmalıdır.
              </Step>
              <Step n={4} title="Google Business Profile'ı Güçlendirmek">
                İşletme açıklaması, kategori, hizmetler, fotoğraflar, yorumlar ve güncellemeler
                düzenli şekilde optimize edilmelidir.
              </Step>
              <Step n={5} title="Yorum ve Güven Sinyallerini Artırmak">
                Müşteri yorumları, puanlar ve kullanıcı deneyimleri yapay zekâların güven
                değerlendirmesinde önemli rol oynar.
              </Step>
              <Step n={6} title="Tutarlı Dijital Varlık Oluşturmak">
                İşletmenin adı, adresi, telefon numarası, hizmet açıklaması ve kategori bilgileri
                tüm platformlarda tutarlı olmalıdır.
              </Step>
              <Step n={7} title="Marka Bahsini Artırmak">
                Markanızın farklı güvenilir kaynaklarda geçmesi, yapay zekâ sistemlerinin sizi daha
                iyi tanımasına yardımcı olabilir.
              </Step>
            </Section>

            <Section icon={<Bot size={18} />} title="uSeen GEO Sürecinde Ne Yapar?">
              <p>
                <strong className="text-white">uSeen</strong>, işletmenizin GEO açısından ne durumda
                olduğunu analiz eder. Yani sadece "web siteniz var mı?" diye bakmaz. Daha geniş bir
                dijital görünürlük değerlendirmesi yapar.
              </p>
              <p>uSeen ile şunları görebilirsiniz:</p>
              <BulletList items={[
                "Yapay zekâlar işletmenizi doğru anlıyor mu?",
                "Hangi sorgularda görünme ihtimaliniz düşük?",
                "Rakipleriniz hangi konularda sizden güçlü?",
                "Web sitenizde hangi bilgiler eksik?",
                "Hangi müşteri niyetlerinde görünürlük zayıf?",
                "Hangi soru-cevap içerikleri üretilmeli?",
                "Dijital varlığınızda tutarsızlık var mı?",
                "Hangi aksiyonlar öncelikli yapılmalı?",
              ]} />
              <p>
                uSeen, GEO çalışmalarını karmaşık bir analiz olmaktan çıkarır ve işletmeler için
                <strong className="text-white"> uygulanabilir görevlere dönüştürür.</strong>
              </p>
              <p className="text-sm text-slate-400">
                Daha fazla okumak ister misiniz?{" "}
                <Link to="/ai-visibility-nedir" className="text-cyan-300 hover:text-cyan-200 underline underline-offset-2" data-testid="geo-internal-link-ai-visibility">
                  AI Visibility Nedir?
                </Link>{" "}
                yazımıza göz atın.
              </p>
            </Section>

            <Section icon={<Rocket size={18} />} title="GEO'nun Geleceği">
              <p>
                Bugün insanlar Google'da arama yapıyor. Ancak giderek daha fazla kullanıcı kararlarını
                yapay zekâ destekli cevaplarla veriyor.
              </p>
              <p>
                Bu değişim markalar için yeni bir görünürlük savaşı başlatıyor.
              </p>
              <p>
                Gelecekte kullanıcılar sadece "arama sonuçlarında kim birinci?" diye bakmayacak.
                Yapay zekâların hangi markaları ve işletmeleri önerdiği daha değerli hale gelecek.
              </p>
              <p>
                Bu yüzden <strong className="text-white">GEO, SEO'nun alternatifi değil; yeni dönemin
                tamamlayıcı görünürlük stratejisidir.</strong>
              </p>
            </Section>

            <Section icon={<CheckCircle2 size={18} />} title="Sonuç">
              <p>
                GEO, markaların ve işletmelerin yapay zekâ destekli cevap motorlarında görünür olmasını
                sağlayan yeni optimizasyon yaklaşımıdır.
              </p>
              <p>
                SEO nasıl yıllarca Google'da görünür olmanın temel yolu olduysa, GEO da yapay zekâ
                çağında önerilebilir olmanın temel yollarından biri haline geliyor.
              </p>
              <p>
                uSeen, işletmelerin bu yeni döneme hazırlanmasını sağlar.
              </p>
              <p className="text-white font-medium">
                Bugünün arama motorları artık yapay zekalar. Sen de önerilerde öne çık, görünür ol.
              </p>
            </Section>
          </Article>

          {/* CTA */}
          <div
            className="mt-12 rounded-3xl border border-cyan-500/20 bg-cyan-500/5 p-7 sm:p-10 relative overflow-hidden"
            data-testid="geo-cta-block"
          >
            <div className="absolute -top-20 -right-20 w-60 h-60 bg-cyan-500/15 blur-3xl rounded-full pointer-events-none" />
            <div className="relative">
              <h3 className="font-display text-2xl sm:text-3xl font-bold text-white mb-2">
                GEO hazırlık seviyenizi ölçün
              </h3>
              <p className="text-slate-300 leading-relaxed max-w-2xl mb-6">
                İşletmenizin yapay zekâ cevap motorlarında ne kadar anlaşılır ve önerilebilir olduğunu
                görmek için ücretsiz kontrol ile başlayın.
              </p>
              <div className="flex flex-wrap gap-3">
                <Link
                  to="/kayit"
                  className="neon-btn rounded-xl px-5 py-3 text-white font-semibold inline-flex items-center gap-2"
                  data-testid="geo-cta-primary"
                >
                  Ücretsiz Başla <ArrowRight size={14} />
                </Link>
                <Link
                  to="/ai-visibility-nedir"
                  className="ghost-btn rounded-xl px-5 py-3 text-white border border-white/10 inline-flex items-center gap-2"
                  data-testid="geo-cta-secondary"
                >
                  AI Visibility Nedir?
                </Link>
              </div>
            </div>
          </div>

          {/* FAQ */}
          <section className="mt-16" data-testid="geo-faq">
            <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-purple-400 mb-3">
              <HelpCircle size={11} /> Sık Sorulan Sorular
            </div>
            <h2 className="font-display text-2xl sm:text-3xl font-bold text-white mb-6">
              GEO hakkında merak edilenler
            </h2>
            <div className="space-y-3">
              {FAQ_ITEMS.map((f, i) => (
                <details
                  key={i}
                  className="group rounded-2xl border border-white/5 bg-black/30 px-5 py-4 open:bg-white/[0.03] transition-colors"
                  data-testid={`faq-item-${i}`}
                >
                  <summary className="cursor-pointer list-none flex items-center justify-between gap-4 text-white font-medium">
                    <span>{f.q}</span>
                    <span className="text-cyan-300 text-xl leading-none group-open:rotate-45 transition-transform">+</span>
                  </summary>
                  <p className="text-slate-300 leading-relaxed mt-3">{f.a}</p>
                </details>
              ))}
            </div>
            <div className="mt-8 text-sm text-slate-400 text-center">
              Sorularınız mı var?{" "}
              <Link to="/iletisim" className="text-cyan-300 hover:text-cyan-200" data-testid="geo-contact-link">
                Bizimle iletişime geçin
              </Link>
              .
            </div>
          </section>
        </div>
      </main>

      <SiteFooter />
    </div>
  );
}

function Article({ children }) {
  return <article className="space-y-10 text-slate-300 leading-relaxed">{children}</article>;
}

function Section({ icon, title, children }) {
  return (
    <section>
      <h2 className="font-display text-2xl sm:text-[1.7rem] font-bold text-white mb-3 flex items-center gap-2.5">
        {icon && <span className="text-cyan-300">{icon}</span>}
        {title}
      </h2>
      <div className="space-y-3 text-[0.975rem]">{children}</div>
    </section>
  );
}

function BulletList({ items }) {
  return (
    <ul className="space-y-1.5 my-3">
      {items.map((item, i) => (
        <li key={i} className="flex items-start gap-2.5">
          <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-cyan-400 shrink-0" />
          <span>{item}</span>
        </li>
      ))}
    </ul>
  );
}

function ComparisonCard({ badge, badgeColor, title, desc }) {
  return (
    <div className="rounded-xl border border-white/5 bg-black/30 p-4">
      <div className={`inline-block text-[10px] uppercase tracking-wider px-2 py-0.5 rounded border ${badgeColor} mb-2`}>
        {badge}
      </div>
      <div className="text-white text-sm font-medium mb-1">{title}</div>
      <div className="text-xs text-slate-400 leading-relaxed">{desc}</div>
    </div>
  );
}

/** Numbered H3 sub-step under "GEO İçin Ne Yapmak Gerekir?" */
function Step({ n, title, children }) {
  return (
    <div className="rounded-xl border border-white/5 bg-black/20 p-4 sm:p-5 my-3">
      <h3 className="font-display text-base sm:text-lg font-semibold text-white mb-1.5 flex items-center gap-2.5">
        <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-cyan-500/15 border border-cyan-500/30 text-cyan-300 text-xs font-bold shrink-0">
          {n}
        </span>
        {title}
      </h3>
      <p className="text-slate-300 text-[0.95rem] leading-relaxed pl-[2.4rem]">{children}</p>
    </div>
  );
}
